import { Component, OnInit } from '@angular/core';
import { DecimalPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { NotificationService } from '../../../shared/components/ui/notification/notification.service';
import { MultiSelectComponent, Option } from '../../../shared/components/form/multi-select/multi-select.component';
import { DatePickerComponent } from '../../../shared/components/form/date-picker/date-picker.component';
import { OtInputComponent } from '../../../shared/components/form/ot-input/ot-input.component';

import { ReportService } from '../../../services/report.service';
import { ClientService } from '../../../services/client.service';
import { TransferReasonService } from '../../../services/transfer-reason.service';

import {
    RemissionGuideReportResponse,
    RemissionGuideReportRowResponse,
} from '../../../dto/remission-guide-report.response';

@Component({
    selector: 'app-remission-guides-report',
    standalone: true,
    imports: [
        NgClass,
        DecimalPipe,
        FormsModule,
        PageBreadcrumbComponent,
        ButtonComponent,
        MultiSelectComponent,
        DatePickerComponent,
        OtInputComponent,
    ],
    templateUrl: './remission-guides-report.component.html',
})
export class RemissionGuidesReportComponent implements OnInit {

    // ── Filters ────────────────────────────────────────────
    startDate = this.defaultStartDate();
    endDate = this.defaultEndDate();

    clientOptions: Option[] = [];
    reasonOptions: Option[] = [];
    selectedClientIds: string[] = [];
    selectedReasonIds: string[] = [];
    otNumber = '';
    status = '';

    readonly statusOptions = [
        { value: '', label: 'Todos' },
        { value: 'PENDIENTE', label: 'Pendiente' },
        { value: 'ACEPTADO', label: 'Aceptado' },
        { value: 'RECHAZADO', label: 'Rechazado' },
    ];

    /** Flag para destruir/recrear los multi-selects al limpiar */
    multiSelectVisible = true;

    // ── State ──────────────────────────────────────────────
    loading = false;
    isExporting = false;
    hasSearched = false;
    reportMeta?: Pick<RemissionGuideReportResponse, 'companyName' | 'dateRange' | 'totalItems'>;
    rows: RemissionGuideReportRowResponse[] = [];

    // ── Search / sort / pagination ─────────────────────────
    searchTerm = '';
    sortColumn = '';
    sortDir: 'asc' | 'desc' = 'asc';

    currentPage = 1;
    pageSize = 10;
    readonly pageSizeOptions = [5, 10, 15, 20, 50];

    constructor(
        private reportService: ReportService,
        private clientService: ClientService,
        private transferReasonService: TransferReasonService,
        private notify: NotificationService,
    ) { }

    ngOnInit(): void {
        this.clientService.getAll().subscribe({
            next: res => {
                this.clientOptions = (res.data ?? []).map(c => ({
                    value: String(c.id),
                    text: c.businessName?.trim() || `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim(),
                }));
            },
            error: () => this.notify.error('No se pudieron cargar los clientes'),
        });
        this.transferReasonService.getAll({ status: 1 }).subscribe({
            next: res => {
                this.reasonOptions = (res.data ?? []).map(r => ({ value: String(r.id), text: r.name }));
            },
            error: () => this.notify.error('No se pudieron cargar los motivos de traslado'),
        });
    }

    // ── Filtered / sorted / paged data ────────────────────
    get filteredRows(): RemissionGuideReportRowResponse[] {
        const term = this.searchTerm.trim().toLowerCase();
        let list = term
            ? this.rows.filter(r =>
                r.guide.toLowerCase().includes(term) ||
                r.client.toLowerCase().includes(term) ||
                (r.otNumber ?? '').toLowerCase().includes(term) ||
                (r.transferReasonName ?? '').toLowerCase().includes(term) ||
                (r.originName ?? '').toLowerCase().includes(term) ||
                r.originAddress.toLowerCase().includes(term) ||
                (r.destinationAddress ?? '').toLowerCase().includes(term) ||
                r.status.toLowerCase().includes(term)
            )
            : this.rows;

        if (this.sortColumn) {
            list = [...list].sort((a, b) => {
                let va = '';
                let vb = '';
                if (this.sortColumn === 'guia') { va = a.guide; vb = b.guide; }
                if (this.sortColumn === 'ot') { va = a.otNumber ?? ''; vb = b.otNumber ?? ''; }
                if (this.sortColumn === 'cliente') { va = a.client; vb = b.client; }
                return this.sortDir === 'asc'
                    ? va.localeCompare(vb, 'es', { numeric: true })
                    : vb.localeCompare(va, 'es', { numeric: true });
            });
        }
        return list;
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    }

    get pageItems(): (number | '...')[] {
        const total = this.totalPages;
        const current = this.currentPage;
        const pages = new Set<number>();
        for (let i = 1; i <= Math.min(3, total); i++) pages.add(i);
        for (let i = Math.max(1, total - 2); i <= total; i++) pages.add(i);
        for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
        const sorted = Array.from(pages).sort((a, b) => a - b);
        const result: (number | '...')[] = [];
        for (let i = 0; i < sorted.length; i++) {
            if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
            result.push(sorted[i]);
        }
        return result;
    }

    get currentItems(): RemissionGuideReportRowResponse[] {
        const start = (this.currentPage - 1) * this.pageSize;
        return this.filteredRows.slice(start, start + this.pageSize);
    }

    // ── Actions ────────────────────────────────────────────
    onFilter(): void {
        if (!this.startDate || !this.endDate) {
            this.notify.warning('Debe ingresar fecha de inicio y fecha fin', 'Validación');
            return;
        }
        if (this.startDate > this.endDate) {
            this.notify.warning('La fecha de inicio no puede ser mayor a la fecha fin', 'Validación');
            return;
        }

        this.loading = true;
        this.hasSearched = true;
        this.currentPage = 1;

        this.reportService.getRemissionGuidesReport(this.startDate, this.endDate, {
            clientIds: this.selectedClientIds.length ? this.selectedClientIds.join(',') : undefined,
            transferReasonIds: this.selectedReasonIds.length ? this.selectedReasonIds.join(',') : undefined,
            otNumber: this.otNumber.trim() || undefined,
            status: this.status || undefined,
        }).subscribe({
            next: res => {
                const data = res.data!;
                this.reportMeta = {
                    companyName: data.companyName,
                    dateRange: data.dateRange,
                    totalItems: data.totalItems,
                };
                this.rows = data.rows ?? [];
                this.loading = false;
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'Error al generar el reporte');
                this.loading = false;
            },
        });
    }

    onClearFilters(): void {
        this.startDate = this.defaultStartDate();
        this.endDate = this.defaultEndDate();
        this.selectedClientIds = [];
        this.selectedReasonIds = [];
        this.otNumber = '';
        this.status = '';
        this.multiSelectVisible = false;
        setTimeout(() => { this.multiSelectVisible = true; }, 0);
        this.searchTerm = '';
        this.rows = [];
        this.reportMeta = undefined;
        this.hasSearched = false;
        this.currentPage = 1;
    }

    toggleSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDir = 'asc';
        }
    }

    setSearchTerm(value: string): void {
        this.searchTerm = (value ?? '').trim().toLowerCase();
        this.currentPage = 1;
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages) this.currentPage = page;
    }

    onPageSizeChange(size: number): void {
        this.pageSize = size;
        this.currentPage = 1;
    }

    // ── Excel export (mismas columnas que la tabla) ────────
    async exportToExcel(): Promise<void> {
        if (!this.rows.length) {
            this.notify.warning('No hay datos para exportar', 'Sin datos');
            return;
        }
        this.isExporting = true;
        try {
            const wb = new ExcelJS.Workbook();
            wb.creator = this.reportMeta?.companyName ?? 'IDMH Perú';
            wb.created = new Date();
            const ws = wb.addWorksheet('Reporte de Guías');

            ws.columns = [
                { key: 'issueDate', width: 12 },
                { key: 'guide', width: 16 },
                { key: 'ot', width: 12 },
                { key: 'client', width: 34 },
                { key: 'reason', width: 26 },
                { key: 'origin', width: 40 },
                { key: 'destination', width: 40 },
                { key: 'weight', width: 12 },
                { key: 'status', width: 14 },
            ];

            ws.mergeCells('A1:G1');
            const cn = ws.getCell('A1');
            cn.value = this.reportMeta?.companyName ?? '';
            cn.font = { bold: true, size: 14, color: { argb: 'FF1e3a5f' } };

            const now = new Date();
            const gd = ws.getCell('I1');
            gd.value = `Generado: ${now.toLocaleDateString('es-PE')} ${now.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}`;
            gd.font = { size: 8, italic: true, color: { argb: 'FF9CA3AF' } };
            gd.alignment = { horizontal: 'right' };

            ws.mergeCells('A2:I2');
            const title = ws.getCell('A2');
            title.value = 'REPORTE DE GUÍAS DE REMISIÓN';
            title.font = { bold: true, size: 12, color: { argb: 'FF1e40af' } };
            title.alignment = { horizontal: 'center' };

            ws.mergeCells('A3:I3');
            const period = ws.getCell('A3');
            period.value = `Período: ${this.reportMeta?.dateRange ?? `${this.startDate} al ${this.endDate}`}  ·  Registros: ${this.reportMeta?.totalItems ?? this.rows.length}`;
            period.font = { size: 9, color: { argb: 'FF6B7280' } };
            period.alignment = { horizontal: 'center' };

            const HEADERS = ['Fecha', 'Guía', 'OT IDMH', 'Destinatario', 'Motivo', 'Punto de partida', 'Punto de llegada', 'Peso', 'Estado'];
            const HDR_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1d4ed8' } } as ExcelJS.Fill;
            const THIN: Partial<ExcelJS.Borders> = {
                top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
                left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
                bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
                right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            };
            HEADERS.forEach((h, i) => {
                const cell = ws.getCell(5, i + 1);
                cell.value = h;
                cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
                cell.fill = HDR_FILL;
                cell.border = THIN;
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });

            this.rows.forEach((r, idx) => {
                const rowNum = 6 + idx;
                const fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: idx % 2 === 0 ? 'FFF9FAFB' : 'FFFFFFFF' } } as ExcelJS.Fill;
                const values: [any, ExcelJS.Alignment['horizontal'], string][] = [
                    [r.issueDate, 'left', '@'],
                    [r.guide, 'left', '@'],
                    [r.otNumber ?? '', 'center', '@'],
                    [r.client, 'left', '@'],
                    [r.transferReasonName ?? '', 'left', '@'],
                    [this.originLabel(r), 'left', '@'],
                    [r.destinationAddress ?? '', 'left', '@'],
                    [r.grossWeight ?? '', 'right', '#,##0.00'],
                    [r.status, 'center', '@'],
                ];
                values.forEach(([val, align, fmt], i) => {
                    const cell = ws.getCell(rowNum, i + 1);
                    cell.value = val;
                    cell.fill = fill;
                    cell.font = { size: 9 };
                    cell.border = THIN;
                    cell.alignment = { vertical: 'middle', horizontal: align };
                    cell.numFmt = fmt;
                });
            });

            const buf = await wb.xlsx.writeBuffer();
            const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const safeRange = (this.reportMeta?.dateRange ?? `${this.startDate}_${this.endDate}`).replace(/[/\\]/g, '-');
            saveAs(blob, `Reporte Guias - ${safeRange}.xlsx`);
        } catch (err) {
            console.error(err);
            this.notify.error('Error al generar el Excel');
        } finally {
            this.isExporting = false;
        }
    }

    // ── Helpers ────────────────────────────────────────────
    /** Nombre del punto de partida si se eligió uno del maestro; si no, la dirección escrita. */
    originLabel(r: RemissionGuideReportRowResponse): string {
        return r.originName || r.originAddress;
    }

    statusClass(status: string | null): string {
        switch (status?.toUpperCase()) {
            case 'ACEPTADO': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            case 'RECHAZADO': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
            case 'PENDIENTE': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
            default: return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
        }
    }

    private defaultStartDate(): string {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    }

    private defaultEndDate(): string {
        return new Date().toISOString().split('T')[0];
    }
}
